import { uploadToS3 } from '../../utilties/uploadToS3'
import {
  useGetOrderBoqs,
  useCreateBoq,
  useGetBoqFiles,
  useProcessBoq,
  useAddClosestItemsToOrder } from './hooks'
import apiConfig from '../../../config/apiConfig'

import React, { useState, useRef } from 'react'
import { Row, Col, Button, Table, Card, Spin, Input } from 'antd'
import { ShoppingCartOutlined } from '@ant-design/icons';

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

const PreviewImages = ({ previews }) => {
  return (
    <Row>
      {previews.map((preview, index) => (
        <Col key={index} span={6}>
          <img src={preview} alt={`Preview ${index}`} style={{ width: '200px', height: 'auto' }} />
        </Col>
      ))}
    </Row>
  )
}

const BoqList = ({ boqs, setBoqId }) => {
  if (!boqs || !boqs.length) return <div>No BOQs found for this order.</div>

  return boqs.map((boq) => {
    const { id, totalCount } = boq || {}

    return (
      <Row key={id} style={{ alignItems: 'center', marginBottom: '8px' }}>
        <Button
          type="primary"
          onClick={() => setBoqId(id)}
        >
          BOQ ID: {id} ({totalCount} files)
        </Button>
      </Row>
    )
  })
}

const UploaderSection = ({ orderNumber }) => {
  const [files, setFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [uploading, setUploading] = useState(false)
  const [boqContext, setBoqContext] = useState(null)
  const [boqText, setBoqText] = useState(null)
  const fileInputRef = useRef(null)

  const [createBoq] = useCreateBoq()
  const [processBoq] = useProcessBoq()

  return (
    <Col>
      <Row>
        <h4>Upload BOQ(s)</h4>
      </Row>
      <Row style={{ marginBottom: '16px' }}>
        <Input.TextArea
          placeholder='BOQ by Text'
          value={boqText}
          onChange={(e) => setBoqText(e.target.value)}
        />
      </Row>
      <Row style={{ marginBottom: '16px' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg, .jpeg, .png, .webp, .heic, .heif, .pdf"
          multiple
          onChange={async (e) => {
            const files = Array.from(e.target.files)
            setFiles(files)
            // // REF: https://stackoverflow.com/questions/37576685/using-async-await-with-a-foreach-loop
            for (const file of files) {
              const base64 = await getBase64(file)
              setPreviews((prev) => [...prev, base64])
            }
          }}
        />
      </Row>
      <Row>
        { uploading
          ? <p>Uploading...</p>
          : <PreviewImages previews={previews} />
        }
      </Row>
      <Row>
        <Input.TextArea
          placeholder='BOQ Context'
          value={boqContext}
          onChange={(e) => setBoqContext(e.target.value)}
        />
        {(files.length > 0 || boqText) && (
          <Button
            type="primary"
            onClick={async () => {
              setUploading(true)
              try {
                const uploadPromises = files.map((file) => uploadToS3(file))
                const result = await Promise.all(uploadPromises)
                const { data } = await createBoq({
                  variables: {
                    orderNumber: orderNumber, // Replace with actual order number
                    boqFileUrls: result,
                    boqText: boqText,
                    boqContext: boqContext,
                  },
                })
                const { createBoq: createBoqData } = data || {}
                const { boq } = createBoqData || {}
                const { id } = boq || {}
                if (!id) {
                  throw new Error('Failed to create BOQ')
                }
                // Process the BOQ after creation
                const processResult = await processBoq({
                  variables: {
                    boqId: id,
                  },
                })
                const { processBoq: processBoqData } = processResult.data || {}
                const { errors } = processBoqData || {}
                if (errors && errors.length > 0) {
                  throw new Error(errors[0].message)
                }
                alert('Files uploaded successfully!')
              } catch (error) {
                alert(`Error uploading files: ${error.message}`)
              } finally {
                setFiles([])
                setPreviews([])
                setUploading(false)
                setBoqContext(null)
                if (fileInputRef.current) {
                  fileInputRef.current.value = null
                }
              }
            }}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Submit'}
          </Button>
        )}
      </Row>
    </Col>
  )
}

const BoqItemList = ({ boqItems, handleCreateRequestItem }) => {
  const [loadingItem, setLoadingItem] = useState(null);

  if (!boqItems || !boqItems.length) return <div>No BOQ items found.</div>

  return boqItems.map((boqItem, index) => {
    const { id: boqItemId, name, searchQuery, qty, unit, remark, versionCode, boqItemClosestInference, boqItemAlternativeInferences } = boqItem || {}
    
    const boqItemDataSource = [
      {
        key: boqItemId,
        name,
        searchQuery,
        qty,
        unit,
        remark,
        versionCode,
      }
    ];

    const inferenceDataSource = [];

    if (boqItemClosestInference) {
      const { id: inferenceId, qty: inferenceQty, unitConversionRemark, item: inferenceItem } = boqItemClosestInference;
      const { id: itemId, name: itemName, unit: itemUnit, url, nameEn, nameTh, cost, weight, price } = inferenceItem || {};
      
      inferenceDataSource.push({
        key: 'closest-' + inferenceId,
        name: itemName,
        itemId,
        qty: inferenceQty,
        unit: itemUnit,
        remark: unitConversionRemark,
        isClosestMatch: true,
        index: 1,
        url: apiConfig.apiBase + url,
        nameEn,
        nameTh,
        cost: cost || 0,
        weight: weight || 0,
        price: price || 0,
        boqItemId,
      });
    }

    if (boqItemAlternativeInferences && boqItemAlternativeInferences.length) {
      boqItemAlternativeInferences.forEach((inference, inferenceIndex) => {
        const { id: inferenceId, qty: inferenceQty, unitConversionRemark, item: inferenceItem } = inference;
        const { id: itemId, name: itemName, unit: itemUnit, url, nameEn, nameTh, cost, weight, price } = inferenceItem || {};
        
        inferenceDataSource.push({
          key: 'alternative-' + inferenceId,
          name: itemName,
          itemId,
          qty: inferenceQty,
          unit: itemUnit,
          remark: unitConversionRemark,
          isAlternative: true,
          index: (boqItemClosestInference ? 2 : 1) + inferenceIndex,
          url: apiConfig.apiBase + url,
          nameEn,
          nameTh,
          cost: cost || 0,
          weight: weight || 0,
          price: price || 0,
          boqItemId,
        });
      });
    }

    const boqItemColumns = [
      {
        title: 'BOQ Item Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Search Query',
        dataIndex: 'searchQuery',
        key: 'searchQuery',
      },
      {
        title: 'Qty',
        dataIndex: 'qty',
        key: 'qty'
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit'
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        key: 'remark'
      },
      {
        title: 'Version Code',
        dataIndex: 'versionCode',
        key: 'versionCode',
      }
    ];

    const inferenceColumns = [
      {
        title: '#',
        dataIndex: 'index',
        key: 'index',
      },
      {
        title: 'Item Name',
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => record.url ? <a href={record.url} target="_blank" rel="noopener noreferrer">{text}</a> : text,
      },
      {
        title: 'Item ID',
        dataIndex: 'itemId',
        key: 'itemId'
      },
      {
        title: 'Qty',
        dataIndex: 'qty',
        key: 'qty'
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit'
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        key: 'remark'
      },
      {
        title: 'Action',
        key: 'action',
        render: (text, record) => (
          <Button
            size="small"
            type="primary"
            icon={loadingItem === record.key ? <Spin /> : <ShoppingCartOutlined />}
            onClick={async () => {
              setLoadingItem(record.key);
              try {
                const { itemId, nameEn, nameTh, cost, qty, unit, weight, price, boqItemId } = record || {}
                handleCreateRequestItem({
                  item_external_id: itemId,
                  name_en: nameEn,
                  name_th: nameTh,
                  cost: cost || 0,
                  quantity: qty,
                  single_price: price || 0,
                  unit,
                  weight: weight || 0,
                  boq_item_id: boqItemId
                }, true)
              } finally {
                setLoadingItem(null);
              }
            }}
            disabled={loadingItem !== null}
          >
          </Button>
        ),
      },
    ];

    return (
      <Card
        key={boqItemId}
        title={
          <>
            <Table 
              dataSource={boqItemDataSource} 
              columns={boqItemColumns} 
              pagination={false} 
              bordered
              size="small"
              showHeader={true}
            />
          </>
        }
        style={{ marginBottom: '16px' }}
      >
        {inferenceDataSource.length > 0 ? (
          <Card title="Inferences">
            <Table 
              dataSource={inferenceDataSource} 
              columns={inferenceColumns} 
              pagination={false} 
              rowClassName={(record) => {
                if (record.isClosestMatch) return 'closest-match-row';
                if (record.isAlternative) return 'alternative-row';
                return '';
              }}
              bordered
              size="small"
            />
          </Card>
        ): (
          <p>No inferences found for this BOQ item.</p>
        )}
        <style>
          {`
            .closest-match-row {
              background-color: #f6ffed !important;
            }
            .closest-match-row td {
              font-weight: bold !important;
            }
            .alternative-row {
              background-color: #f5f5f5 !important;
            }
          `}
        </style>
      </Card>
    );
  });
};

const BoqFileThumbnails = ({ boqFiles }) => {
  if (!boqFiles || !boqFiles.length) return <div>No BOQ files found.</div>

  return (
    <Row style={{ marginBottom: '16px', gap: '8px' }}>
      {boqFiles.map((file) => {
        const { id, value, contentType } = file || {}
        if (contentType === 'text') {
          return <span>{value}</span>
        }

        return (
          <a href={value} target="_blank" rel="noopener noreferrer" key={id}>
            <img src={value} alt={`BOQ File ${id}`} style={{ width: '100px', height: 'auto' }} />
          </a>
        )
      })}
    </Row>
  )
}

const BoqItemsForm = ({ boqId, orderId, handleCreateRequestItem }) => {
  const { loading, error, boqFiles, boqContext } = useGetBoqFiles(boqId)
  const [addingAll, setAddingAll] = useState(false);
  const [addClosestItemsToOrder] = useAddClosestItemsToOrder()

  if (loading) return <p>Loading...</p>
  if (error) return <p>Error: {error.message}</p>

  if (!boqId) return <div>Please select a BOQ to view items.</div>
  if (!boqFiles || !boqFiles.length) return <div>No BOQ files found.</div>

  return (
    <Row gutter={[16, 0]}>
      <Col>
        <Row>
          <Col>
            <h2>BOQ ID: {boqId}</h2>
            <h4 style={{ whiteSpace: 'pre-line' }}>BOQ Context: {boqContext || '-'}</h4>
            <BoqFileThumbnails boqFiles={boqFiles} />
            {boqId && (
              <Button
                type="primary"
                onClick={async () => {
                  setAddingAll(true);
                  try {
                    await addClosestItemsToOrder({
                      variables: {
                        boqId: boqId,
                        orderNumber: orderId,
                      },
                    })
                    alert('All closest match items added! refreshing page...')
                    // Restart page
                    window.location.reload()
                  } catch (error) {
                    alert(`Error adding items: ${error.message}`)
                  } finally {
                    setAddingAll(false);
                  }
                }}
                disabled={addingAll}
              >
                {addingAll ? <Spin /> : "Add all closest match items"}
              </Button>
            )}
            {boqFiles.map((file) => {
              const { id: boqFileId, filename, processStatus, boqItems, errorMessage, value } = file || {}
              return (
                <div key={boqFileId}>
                  <h3>{filename || value} ({processStatus})</h3>
                  {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                  <BoqItemList boqItems={boqItems} handleCreateRequestItem={handleCreateRequestItem} />
                </div>
              )})
            }
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export const BoqItemFormContainer = ({ orderId, handleCreateRequestItem }) => {
  const [boqId, setBoqId] = useState(null)

  const { loading, error, boqs } = useGetOrderBoqs(orderId)
  if (loading) return <p>Loading...</p>
  if (error) return <p>Error: {error.message}</p>

  return (
    <Row gutter={[16, 0]}>
      <Col span={6} style={{ borderRight: '1px solid' }}>
        <Row>
          <Col>
            <h2>BOQs Order: {orderId}</h2>
            <BoqList boqs={boqs} setBoqId={setBoqId} />
          </Col>
        </Row>
        <hr />
        <Row>
          <UploaderSection orderNumber={orderId} />
        </Row>
      </Col>
      {/* Right Column */}
      <Col span={18}>
        <BoqItemsForm boqId={boqId} orderId={orderId} handleCreateRequestItem={handleCreateRequestItem} />
      </Col>
    </Row>
  )
}